import { defineStore } from "pinia";
import { computed, readonly, ref } from "vue";

import { TPilotingPlanningScaling } from "@/components/piloting/planning/piloting-planning.types";
import $date, { EDateScaling } from "@/utils/date";
import { assertUnreachable } from "@/utils/types-utils";

export const useProgrammingPlanningStore = defineStore("programmingPlanningStore", () => {
  const planningRangeScale = ref<TPilotingPlanningScaling>(EDateScaling.WEEK);
  const TODAY = new Date();
  const date = ref<Date>(TODAY);

  const startDate = computed<Date>(() => {
    switch (planningRangeScale.value) {
      case EDateScaling.DAY:
        return $date.startOf(date.value, EDateScaling.DAY);
      case EDateScaling.WEEK:
        return $date.startOf(date.value, EDateScaling.WEEK);
      case EDateScaling.MONTH:
        return $date.startOf(date.value, EDateScaling.MONTH);
      case EDateScaling.QUARTER:
        return $date.startOf(date.value, EDateScaling.QUARTER);
      default:
        assertUnreachable(planningRangeScale.value);
    }
  });

  const endDate = computed<Date>(() => {
    switch (planningRangeScale.value) {
      case EDateScaling.DAY:
        return $date.endOf(date.value, EDateScaling.DAY);
      case EDateScaling.WEEK:
        return $date.endOf(date.value, EDateScaling.WEEK);
      case EDateScaling.MONTH:
        return $date.endOf(date.value, EDateScaling.MONTH);
      case EDateScaling.QUARTER:
        return $date.endOf(date.value, EDateScaling.QUARTER);
      default:
        assertUnreachable(planningRangeScale.value);
    }
  });

  const days = computed<Date[]>(() => {
    const result: Date[] = [];

    for (let d = startDate.value; d <= endDate.value; d = $date.add(d, 1, EDateScaling.DAY)) {
      result.push(new Date(d));
    }

    return result;
  });

  function getMonthProportion(monthStart: Date) {
    const nbDaysInMonth = $date.getLastDayOfMonth(monthStart.getFullYear(), monthStart.getMonth());

    return nbDaysInMonth / totalDaysInDateRange.value;
  }

  const months = computed<Array<{ monthStartDate: Date; proportion: number }>>(() => {
    const result = [];

    const firstMonthStart = $date.startOf(startDate.value, EDateScaling.MONTH);
    const lastMonthStart = $date.startOf(endDate.value, EDateScaling.MONTH);

    for (let d = firstMonthStart; d <= lastMonthStart; d.setMonth(d.getMonth() + 1)) {
      result.push({
        monthStartDate: new Date(d),
        proportion: getMonthProportion(d),
      });
    }

    return result;
  });

  const totalDaysInDateRange = computed(() => $date.diff(endDate.value, startDate.value));

  function navigateToNextRange() {
    const startOf = $date.startOf(date.value, planningRangeScale.value);
    date.value = $date.add(startOf, 1, planningRangeScale.value);
  }

  function navigateToPreviousRange() {
    const startOf = $date.startOf(date.value, planningRangeScale.value);
    date.value = $date.subtract(startOf, 1, planningRangeScale.value);
  }

  function resetDate() {
    date.value = TODAY;
  }

  return {
    planningRangeScale,
    date: readonly(date),
    today: TODAY,
    startDate,
    endDate,
    days,
    months,
    totalDaysInDateRange,
    navigateToNextRange,
    navigateToPreviousRange,
    resetDate,
  };
});
